/**
 * Tucoiva Product Inquiry
 * Heritage product inquiry: native Shopify contact form and WhatsApp.
 */
(() => {
  const TAG_NAME = 'tucoiva-product-inquiry';

  if (customElements.get(TAG_NAME)) return;

  class TucoivaProductInquiry extends HTMLElement {
    constructor() {
      super();
      this.handleOpen = this.handleOpen.bind(this);
      this.handleWhatsApp = this.handleWhatsApp.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleDialogKeydown = this.handleDialogKeydown.bind(this);
      this.handleScopeChange = this.handleScopeChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleClose = () => this.closeDialog();
      this.handlePageShow = () => this.resetSubmitState();
      this.handleInvalid = () => this.resetSubmitState();
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.removeAttribute('data-tpi-initialized');

      this.dialog = this.querySelector('[data-tpi-dialog]');
      this.openButton = this.querySelector('[data-tpi-open-dialog]');
      this.whatsappButton = this.querySelector('[data-tpi-whatsapp]');
      this.closeButtons = this.querySelectorAll('[data-tpi-close-dialog]');
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[data-tpi-submit]');
      this.productScope = this.closest('.shopify-section') || document;
      this.variants = this.readVariants();

      this.submitLabel = this.submitButton?.querySelector('span');
      this.idleLabel = this.submitLabel?.textContent || '';
      this.submitStatus = this.querySelector('[data-tpi-submit-status]');

      this.openButton?.addEventListener('click', this.handleOpen);
      this.whatsappButton?.addEventListener('click', this.handleWhatsApp);
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', this.handleClose);
      });
      this.dialog?.addEventListener('click', this.handleDialogClick);
      this.dialog?.addEventListener('keydown', this.handleDialogKeydown);
      this.productScope?.addEventListener('change', this.handleScopeChange, true);
      this.form?.addEventListener('submit', this.handleSubmit);
      this.form?.addEventListener('invalid', this.handleInvalid, true);
      window.addEventListener('pageshow', this.handlePageShow);
      this.resetSubmitState();

      this.initializeCountryField();
      this.updateProductContext();

      const state = this.querySelector('[data-tpi-form-state]');
      if (state?.dataset.openOnLoad === 'true') {
        window.requestAnimationFrame(() => this.openDialog());
      }
    }

    disconnectedCallback() {
      this.openButton?.removeEventListener('click', this.handleOpen);
      this.whatsappButton?.removeEventListener('click', this.handleWhatsApp);
      this.dialog?.removeEventListener('click', this.handleDialogClick);
      this.dialog?.removeEventListener('keydown', this.handleDialogKeydown);
      this.dialog?.removeAttribute('open');
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.productScope?.removeEventListener('change', this.handleScopeChange, true);
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.form?.removeEventListener('invalid', this.handleInvalid, true);
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleClose));
      window.removeEventListener('pageshow', this.handlePageShow);
      this.resetSubmitState();
      this.initialized = false;
    }

    initializeCountryField() {
      const countrySelect = this.querySelector('[data-tpi-country]');
      if (!countrySelect) return;

      const wasEmpty = !countrySelect.value || countrySelect.value === '---';
      countrySelect.querySelectorAll('option[value="---"]').forEach((option) => option.remove());

      let placeholder = countrySelect.querySelector('option[value=""]');
      if (!placeholder) {
        placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.disabled = true;
        countrySelect.prepend(placeholder);
      }

      placeholder.textContent = countrySelect.dataset.placeholder || 'Select a country or region';

      if (wasEmpty) {
        const defaultCountry = (countrySelect.dataset.defaultCountry || '').trim();
        const hasDefaultCountry = Array.from(countrySelect.options).some(
          (option) => option.value === defaultCountry
        );

        countrySelect.value = hasDefaultCountry ? defaultCountry : '';
      }
    }

    readVariants() {
      const source = this.querySelector('[data-tpi-variants]');
      if (!source) return [];

      try {
        const parsed = JSON.parse(source.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.warn('[Product inquiry] Unable to read product variants.', error);
        return [];
      }
    }

    getSelectedVariantId() {
      const productForms = this.productScope?.querySelectorAll('form[action*="/cart/add"]') || [];

      for (const form of productForms) {
        const fields = form.querySelectorAll('[name="id"]');
        for (const field of fields) {
          const isChoice = field.type === 'radio' || field.type === 'checkbox';
          if (isChoice && !field.checked) continue;
          if (field.value) return String(field.value);
        }
      }

      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      return urlVariant || String(this.dataset.defaultVariantId || '');
    }

    getSelectedVariant() {
      const variantId = this.getSelectedVariantId();
      return this.variants.find((variant) => String(variant.id) === variantId) || this.variants[0] || null;
    }

    buildProductUrl(variant) {
      let url;

      try {
        url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
      } catch (_error) {
        url = new URL(window.location.href);
      }

      if (variant?.id) {
        url.searchParams.set('variant', String(variant.id));
      }

      return url.toString();
    }

    getContext() {
      const variant = this.getSelectedVariant();
      const productTitle = this.dataset.productTitle || document.title;
      const variantTitle = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
      const sku = variant?.sku || '';
      const productUrl = this.buildProductUrl(variant);

      return { variant, productTitle, variantTitle, sku, productUrl };
    }

    updateProductContext() {
      const context = this.getContext();
      const setValue = (key, value) => {
        const input = this.querySelector(`[data-tpi-field="${key}"]`);
        if (input) input.value = value || '';
      };

      setValue('product_title', context.productTitle);
      setValue('product_url', context.productUrl);
      setValue('selected_variant', context.variantTitle || 'Default');
      setValue('variant_sku', context.sku);

      const productSummary = this.querySelector('[data-tpi-summary-product]');
      const variantSummary = this.querySelector('[data-tpi-summary-variant]');
      const variantRow = this.querySelector('[data-tpi-summary-variant-row]');
      const productLink = this.querySelector('[data-tpi-summary-link]');

      if (productSummary) productSummary.textContent = context.productTitle;
      if (variantSummary) variantSummary.textContent = context.variantTitle;
      if (variantRow) variantRow.hidden = !context.variantTitle;
      if (productLink) productLink.href = context.productUrl;

      return context;
    }

    buildWhatsAppMessage(context) {
      const lines = [];
      const intro = (this.dataset.whatsappIntro || '').trim();

      if (intro) lines.push(intro);
      lines.push('', `Product: ${context.productTitle}`);
      if (context.variantTitle) lines.push(`Selected option: ${context.variantTitle}`);
      if (context.sku) lines.push(`SKU: ${context.sku}`);
      lines.push(`Product link: ${context.productUrl}`, '', 'My question:');

      return lines.join('\n');
    }

    handleWhatsApp() {
      const number = (this.dataset.whatsappNumber || '').replace(/\D/g, '');
      if (!number) return;

      const context = this.updateProductContext();
      const message = encodeURIComponent(this.buildWhatsAppMessage(context));
      const url = `https://wa.me/${number}?text=${message}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    handleOpen() {
      this.updateProductContext();
      this.openDialog();
    }

    isInlineLayout() {
      // Keep this breakpoint in sync with the component stylesheet.
      return window.matchMedia?.('(max-width: 749px)')?.matches === true;
    }

    openDialog() {
      if (!this.dialog || this.dialog.hasAttribute('open')) return;

      // Keep the form in place and out of the browser's modal top layer.
      // Shopify mounts its interactive hCaptcha window outside this dialog;
      // showModal() would cover it and make it inert regardless of z-index.
      // CSS provides the overlay. Do not move or recreate the bound form.
      this.dialog.setAttribute('aria-modal', 'false');
      if (typeof this.dialog.show === 'function') {
        if (!this.dialog.open) this.dialog.show();
      } else {
        this.dialog.setAttribute('open', '');
      }
      this.openButton?.setAttribute('aria-expanded', 'true');

      window.requestAnimationFrame(() => {
        if (!this.isConnected || !this.dialog.hasAttribute('open')) return;

        // Opening the form must not also open a phone's software keyboard.
        // The mobile panel stays in document flow, so both it and Shopify's
        // external verification window can use normal document scrolling.
        const inline = this.isInlineLayout();
        const touch = window.matchMedia?.('(pointer: coarse)')?.matches === true;
        const heading = this.dialog.querySelector('.tpi-dialog__title');
        if (heading) heading.setAttribute('tabindex', '-1');
        const target = inline || touch ? heading :
          this.dialog.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled])');
        (target || this.dialog.querySelector('button:not([disabled])'))?.focus({ preventScroll: true });
        if (inline) this.dialog.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    }

    closeDialog() {
      if (!this.dialog) return;

      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }

      this.openButton?.setAttribute('aria-expanded', 'false');
      this.openButton?.focus({ preventScroll: true });
    }

    handleDialogClick(event) {
      if (!this.isInlineLayout() && event.target === this.dialog) this.closeDialog();
    }

    hasVisibleCaptcha() {
      // Visibility only: Shopify owns the challenge, token and submission.
      return Array.from(document.querySelectorAll('iframe[src*="hcaptcha.com"]')).some((frame) => {
        if (this.dialog?.contains(frame)) return false;
        const rect = frame.getBoundingClientRect();
        if (!rect.width || !rect.height) return false;
        for (let node = frame; node; node = node.parentElement) {
          const style = window.getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
        }
        return true;
      });
    }

    handleDialogKeydown(event) {
      if (event.key !== 'Escape' && event.key !== 'Tab') return;
      // Never pull focus away from an external verification window.
      if (this.hasVisibleCaptcha()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeDialog();
        return;
      }

      // An expanded mobile form is part of the page, not a focus-trapping modal.
      if (this.isInlineLayout()) return;

      const controls = Array.from(this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'
      )).filter((node) => node.getClientRects().length && !node.closest('[hidden]'));
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    handleScopeChange() {
      window.requestAnimationFrame(() => this.updateProductContext());
    }

    handleSubmit(event) {
      this.updateProductContext();
      this.resetSubmitState();
      // Release the software keyboard before Shopify positions its challenge.
      // Do not move the form or interfere with the challenge/token lifecycle.
      const activeField = document.activeElement;
      if ((this.isInlineLayout() || window.matchMedia?.('(pointer: coarse)')?.matches) &&
          this.form?.contains(activeField) && activeField?.matches('input, textarea, select')) {
        activeField.blur();
      }
      // Shopify's form protection handles this same event on document. Let
      // that handler finish before deciding whether a navigation is underway.
      // Do not preventDefault, AJAX-post, or call form.submit/requestSubmit here.
      this.submitFeedbackTimer = window.setTimeout(() => {
        if (!this.isConnected || event.defaultPrevented) return;
        this.setSubmitBusy(true);
        // This timeout only restores UI. It never retries or reports success:
        // the server may have received a submission even if navigation stalled.
        this.submitRecoveryTimer = window.setTimeout(() => {
          const message = this.submitButton?.dataset.waitMessage ||
            'This is taking longer than expected. Check for a confirmation before sending again.';
          this.resetSubmitState();
          if (this.submitStatus) {
            this.submitStatus.textContent = message;
            this.submitStatus.hidden = false;
          }
        }, 20000);
      }, 0);
    }

    setSubmitBusy(busy) {
      if (!this.submitButton) return;
      this.submitButton.disabled = busy;
      if (busy) this.submitButton.setAttribute('aria-busy', 'true');
      else this.submitButton.removeAttribute('aria-busy');
      if (this.submitLabel) {
        this.submitLabel.textContent = busy ? (this.submitButton.dataset.sendingLabel || 'Sending…') : this.idleLabel;
      }
    }

    resetSubmitState() {
      window.clearTimeout(this.submitFeedbackTimer);
      window.clearTimeout(this.submitRecoveryTimer);
      this.setSubmitBusy(false);
      if (this.submitStatus) {
        this.submitStatus.textContent = '';
        this.submitStatus.hidden = true;
      }
    }
  }

  customElements.define(TAG_NAME, TucoivaProductInquiry);
})();
